<template>
	<div
		class="single-car"
		:data-car-id="car.id"
		:class="{ 'single-car--home': home, 'single-car--white': whiteCard, 'single-car--readonly': readOnly }"
		@click="chooseCar">
		<div class="single-car__top">
			<div class="single-car__title-container">
				<div class="single-car__title">{{ car?.brand }} {{ car?.name }}</div>
				<div v-if="!exactCar" class="single-car__desc" :class="{ 'single-car__desc--fixed-height': home }">
					<span
						>{{ tr.orSimilar }}
						<!-- {{ i18nService.$tDynamic('pages.panel.discount_codes.code_id.segments', segmentsMapTranslations[car?.segment.name]) }} -->
						{{ locale === 'pl' ? car?.segment?.name : car?.segment?.nameEN }}
					</span>

					<div class="single-car__tooltip">
						<CarsSingleCarSegmentTooltip v-model="tooltip" :segment="car.segment.name" :segmentID="car.segment.id" />
					</div>
				</div>
			</div>
			<div v-if="!home" class="single-car__car-info">
				<div class="single-car__car-info-badges">
					<GlobalCarParameter v-for="(param, i) in carParameters" :key="i" :isElectric="isElectricCar" :parameter="param" />
				</div>
			</div>
		</div>
		<div class="single-car__img-container">
			<GlobalImage
				v-if="car?.image"
				class="single-car__img"
				:class="{ 'single-car__img--home': home }"
				:alt="`${car?.brand} ${car?.name}`"
				:src="car?.image"
				sizes="xs:320px sm:640px md:320px lg:320px"
				:loading="loading" />
			<GlobalImage v-else class="single-car__img" :alt="`${car?.brand} ${car?.name}`" src="car-placeholder.png" />
			<div v-if="carYear" class="single-car__year">{{ tr.year }} {{ carYear }}</div>
		</div>

		<div class="single-car__bottom">
			<div v-if="home" class="single-car__car-info">
				<div class="single-car__car-info-badges" :class="{ 'single-car__car-info-badges--fixed-height': home }">
					<GlobalCarParameter v-for="(param, i) in carParameters" :key="i" :isElectric="isElectricCar" :parameter="param" />
				</div>
			</div>
			<div v-else-if="!readOnly" class="single-car__info-badges">
				<GlobalRentParameter v-for="(param, i) in rentParameters" :key="i" :rentParameter="param" />
			</div>
			<div v-if="!readOnly" class="single-car__summary">
				<!-- Regular price -->
				<div v-if="!car.segment.promotionalPrice && car.segment.regularPrice?.amountGross" class="single-car__price-container">
					<div v-if="car.segment && car.segment.regularPrice.amountGross && !home" class="single-car__main-price">
						{{ formatPrice(Number(car.segment.regularPrice.amountGross) * rentDays, 2, true, car.segment.regularPrice.currency) }}
						<span>
							/{{ tr.for }} {{ rentDays }}
							<span v-if="rentDays === 1">{{ tr.day }}</span>
							<span v-else>{{ tr.days }}</span>
						</span>
					</div>
					<div v-else-if="car.segment && car.segment.regularPrice.amountGross" class="single-car__main-price">
						{{ formatPrice(car.segment.regularPrice.amountGross, 2, true, car.segment.regularPrice.currency) }}
						<span>/{{ tr.for }} {{ tr.day }}</span>
					</div>
					<div v-if="mainPriceInPricelistCurrency" class="single-car__price-pricelist" :title="tr.settledInPricelistCurrency">
						* {{ mainPriceInPricelistCurrency }}
					</div>
				</div>
				<!-- Discount price -->
				<div v-else-if="car.segment.regularPrice?.amountGross" class="single-car__price-container">
					<div class="single-car__price-discount">
						<div v-if="!home" class="single-car__price-discount-old">
							{{ formatPrice(Number(car.segment.regularPrice.amountGross) * rentDays, 2, true, car.segment.regularPrice.currency) }}
						</div>
						<div v-else class="single-car__price-discount-old">
							{{ formatPrice(car.segment.regularPrice.amountGross, 2, true, car.segment.regularPrice.currency) }}
						</div>
						<!-- <div class="single-car__price-discount-value">-200 zł</div> -->
					</div>
					<div v-if="car.segment && car.segment.promotionalPrice?.amountGross && !home" class="single-car__main-price">
						{{ formatPrice(Number(car.segment.promotionalPrice.amountGross) * rentDays, 2, true, car.segment.promotionalPrice.currency) }}
						<span>
							/{{ tr.for }} {{ rentDays }}
							<span v-if="rentDays === 1">{{ tr.day }}</span>
							<span v-else>{{ tr.days }}</span>
						</span>
					</div>
					<div v-else-if="car.segment && car.segment.promotionalPrice?.amountGross" class="single-car__main-price">
						{{ formatPrice(car.segment.promotionalPrice.amountGross, 2, true, car.segment.promotionalPrice.currency) }}
						<span>/{{ tr.for }} {{ tr.day }}</span>
					</div>
					<div v-if="mainPriceInPricelistCurrency" class="single-car__price-pricelist" :title="tr.settledInPricelistCurrency">
						* {{ mainPriceInPricelistCurrency }}
					</div>
				</div>
				<ButtonPrimary class="single-car__button">{{ tr.select }}</ButtonPrimary>
			</div>
		</div>
	</div>
	<LazyCarInquiryModal v-if="modalForm" v-model="openModal" :car="car" :min-months="minMonths" :max-months="maxMonths" />
</template>

<script setup lang="ts">
import { differenceInHours } from 'date-fns'

const router = useRouter()
const bookingStore = useBookingStore()
const props = withDefaults(
	defineProps<{
		car: IProductModel
		home?: boolean
		whiteCard?: boolean
		modalForm?: boolean
		minMonths?: number
		maxMonths?: number
		index: number
		noBooker?: boolean
		readOnly?: boolean
		exactCar?: boolean
		loading?: 'eager' | 'lazy' | undefined
	}>(),
	{
		loading: 'lazy',
	},
)
const bookerStore = useBookerStore()
const bookerStartLocation = computed(() => bookingStore.getBookerLocations?.start)
const { tr, locale } = useSingleCarTranslations()
const openModal = ref(false)
const localePath = useLocalePath()
const tooltip = ref(false)
const listName = inject(GTM_LIST_NAME, null)

const emit = defineEmits<{
	(e: 'closeMegaMenu'): void
}>()

function handleOpenModal() {
	openModal.value = true
}

const changeTooltip = (val: boolean) => {
	tooltip.value = val
}

defineExpose<{
	changeTooltip: (val: boolean) => void
}>({ changeTooltip })

const { useDefaultRentDays } = useBookerService()
const defaultRentDays = useDefaultRentDays()

const rentDays = computed(() => {
	return bookingStore.getDays ?? defaultRentDays.value
})

const terminalOrTrainStation = computed(() => {
	if (bookerStartLocation.value?.icon === 'train') return tr.value.onTrainStation
	if (bookerStartLocation.value?.icon === 'airplane') return tr.value.onAirport
	return false
})

const mainPrice = computed(() => props.car.segment.promotionalPrice ?? props.car.segment.regularPrice)

const mainPriceInPricelistCurrency = computed(() => {
	const price = mainPrice.value
	if (!price?.pricelistAmountGross || !price.pricelistCurrency || price.currency === price.pricelistCurrency) return undefined

	const days = rentDays.value
	const amount = !props.home && days ? price.pricelistAmountGross * days : price.pricelistAmountGross
	return formatPrice(amount, 2, true, price.pricelistCurrency)
})

const rentDeposit = props.car.parameters.find((p) => p.name === 'Deposit')
const isElectricCar = props.car.parameters.find((p) => p.name === 'Fuel Type')?.value === 'Elektryczny'

const { filteredParametersByBags } = useParametersService()
const carParameters = computed(() => filteredParametersByBags(props.car.parameters).filter((p) => p.name !== 'Year'))
const carYear = computed(() => props.car.parameters.find((p) => p.name === 'Year')?.value)

const rentParameters = computed(() => {
	const t = tr.value
	const terminal = terminalOrTrainStation.value
	const days = rentDays.value
	const isPickupLocation = bookerStartLocation.value?.isPickupLocation
	return [
		{
			title: rentDeposit ? `${t.deposit} ${formatPrice(rentDeposit.value, 0)}` : t.noDeposit,
			tooltip: null,
			value: locale.value === 'pl' || !!(rentDeposit?.value && Number(rentDeposit.value) > 0),
		},
		{ title: t.freeCancellation, tooltip: null, value: true },
		{
			title: `${t.collectionPointAt} ${terminal}`,
			tooltip: `${t.collectionPointAt} ${terminal}`,
			value: isPickupLocation && !!terminal && days && days < 30,
		},
		{
			title: t.noMileageLimit,
			tooltip: null,
			value: !(isPickupLocation && !!terminal) && !(days && days >= 30),
		},
		{
			title: t.mileageLimitPerDay,
			tooltip: null,
			value: days && days >= 30,
		},
	]
})

const isCarRentableForThisDaysCount = computed(() => {
	const startDate = bookingStore.getBookerDates?.start
	const endDate = bookingStore.getBookerDates?.end
	if (!startDate || !endDate) return true

	const minSegmentDays = props.car.segment.minDays

	const minHours = minSegmentDays ? minSegmentDays * 24 : 0

	if (!minHours) return false
	return differenceInHours(endDate, startDate) >= minHours
})

const chooseCar = () => {
	if (props.readOnly) return
	if (props.modalForm) {
		handleOpenModal()
		return
	}
	if (props.noBooker) {
		router.push(localePath({ name: 'cars' }))
		return
	}

	emit('closeMegaMenu')
	const cookie = useCookie('cf-car')
	if (!bookingStore.getBookerLocations || bookingStore.getBookerLocations.start === null) {
		// $notyf.error('Uzupełnij miejsce odbioru/zwrotu oraz termin wypożyczenia')
		cookie.value = JSON.stringify(props.car)

		bookerStore.setIsStartLocationMenuOpen(true)
	} else {
		cookie.value = null

		if (!isCarRentableForThisDaysCount.value) {
			bookingStore.updateSelectedCar(props.car)
			bookerStore.setIsRedirectToReservationAfterDate(true)
			bookerStore.setIsStartDateMenuOpen(true)
			emit('closeMegaMenu')
		} else {
			bookingStore.updateSelectedCar(props.car)
			router.push(localePath({ name: 'reservation' }))
		}
	}
	sendAnalyticsData()
}

const sendAnalyticsData = () => {
	const gtmService = useGTMService()
	if (!gtmService) return
	const bookingStore = useBookingStore()
	const GTMParameters = {
		car_name: getCarNameLabel(props.car),
		car_brand: props.car.brand || null,
		car_id: props.car.id,
		day_price: Number(getCarPricePerDay(props.car)),
		total_rent: Number(getCarPrice(props.car, bookingStore.getDays)),
		item_list_name: listName,
		listing_position: props.index + 1,
		car_segment: getCarSegmentLabel(props.car),
		car_segment_id: props.car.segment.id,
		car_category: getCarGroups(props.car),
		gearbox: props.car.parameters.find((p) => p.name === 'Transmission')?.value?.toLowerCase(),
		seats: props.car.parameters.find((p) => p.name === 'Seats') ? Number(props.car.parameters.find((p) => p.name === 'Seats')?.value) : null,
		engine: props.car.parameters.find((p) => p.name === 'Fuel Type')?.value?.toLowerCase(),
	}
	gtmService.vehicleClicked(GTMParameters)
}
</script>

<style lang="scss" scoped src="./SingleCar.scss"></style>
